import { ErrorMessage } from './error-message.enum';
const defaultErrorContext = { enabled: true, level: 'error' };
export class BaseError extends Error {
    constructor(error, trackingErrorType) {
        const errorMessage = error instanceof Error ? error.message : error;
        super(errorMessage !== null && errorMessage !== void 0 ? errorMessage : 'Unknown error');
        this.trackingErrorType = trackingErrorType;
    }
    /**
     * Override in subclasses to attach structured data to the tracker payload.
     */
    getTrackingDetails() {
        return undefined;
    }
}
export class WidgetError extends BaseError {
    constructor(id, contexts) {
        var _a, _b;
        const errorMessage = (_b = (_a = contexts === null || contexts === void 0 ? void 0 : contexts.originalError) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : '';
        super(`WidgetError: "${id}" ${errorMessage}`, 7 /* TrackingDataErrorType.WidgetElement */);
        this.name = 'WidgetError';
        this.contexts = contexts;
    }
}
export class VideoError extends BaseError {
    constructor(message, contexts) {
        var _a;
        const videoSource = (_a = contexts === null || contexts === void 0 ? void 0 : contexts.src) !== null && _a !== void 0 ? _a : '';
        super(`VideoError: ${message} ${videoSource}`, 6 /* TrackingDataErrorType.VideoElement */);
        this.name = 'VideoError';
    }
}
export class CustomScriptError extends BaseError {
    constructor(error) {
        super(error, 10 /* TrackingDataErrorType.CustomScript */);
    }
}
export class CreativeLoaderError extends BaseError {
    constructor(error) {
        super(error, 3 /* TrackingDataErrorType.CreativeLoader */);
    }
}
export class CreativeError extends BaseError {
    constructor(error) {
        super(error, 4 /* TrackingDataErrorType.Creative */);
    }
}
export class ElementError extends BaseError {
    constructor(error, errorType) {
        var _a;
        const trackingErrorType = error instanceof Error && 'trackingErrorType' in error
            ? error.trackingErrorType
            : undefined;
        super(error, (_a = trackingErrorType !== null && trackingErrorType !== void 0 ? trackingErrorType : errorType) !== null && _a !== void 0 ? _a : 4 /* TrackingDataErrorType.Creative */);
    }
}
export class AdError extends BaseError {
    constructor(error) {
        super(error, 2 /* TrackingDataErrorType.Ad */);
    }
}
const FONT_ERROR_TEXT_SAMPLE_LIMIT = 25;
const FONT_ERROR_URL_LENGTH_LIMIT = 512;
export class FontError extends BaseError {
    constructor(error, context) {
        super(error, 11 /* TrackingDataErrorType.Font */);
        this.name = 'FontError';
        this.url = context === null || context === void 0 ? void 0 : context.url;
        this.text = context === null || context === void 0 ? void 0 : context.text;
        this.isFeeded = context === null || context === void 0 ? void 0 : context.isFeeded;
    }
    getTrackingDetails() {
        const details = {};
        if (this.url) {
            if (this.url.startsWith('data:')) {
                details.url = '[data: URL omitted]';
            }
            else {
                details.url = this.url.slice(0, FONT_ERROR_URL_LENGTH_LIMIT);
            }
        }
        if (this.text) {
            details.text = this.text.slice(0, FONT_ERROR_TEXT_SAMPLE_LIMIT);
        }
        if (this.isFeeded !== undefined) {
            details.isFeeded = this.isFeeded;
        }
        return Object.keys(details).length ? details : undefined;
    }
}
export class AdInterventionError extends BaseError {
    constructor(error) {
        super(error, 1 /* TrackingDataErrorType.Tracking */);
    }
}
export class FeedError extends BaseError {
    constructor(error) {
        super(error, 9 /* TrackingDataErrorType.FeedElement */);
    }
}
export class ImageError extends BaseError {
    constructor(msg, url, originalError) {
        super(msg, 5 /* TrackingDataErrorType.ImageElement */);
        this.url = url;
        this.originalError = originalError;
    }
}
export function getVideoErrorMessage(code) {
    switch (code) {
        case MediaError.MEDIA_ERR_ABORTED:
            return ErrorMessage.MediaErrorAborted;
        case MediaError.MEDIA_ERR_NETWORK:
            return ErrorMessage.MediaErrorNetwork;
        case MediaError.MEDIA_ERR_DECODE:
            return ErrorMessage.MediaErrorDecode;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            return ErrorMessage.MediaErrorSourceNotSupported;
        default:
            return '';
    }
}
function isErrorContext(value) {
    return !!(value && value.contexts);
}
/**
 * Extract structured tracking data from a thrown value.
 *
 * Uses duck-typing rather than `instanceof BaseError` because errors thrown
 * inside the animated-creative bundle are constructed against that bundle's
 * own copy of BaseError; an `instanceof` check from the host ad bundle would
 * fail and silently drop the details.
 */
export function getTrackingDetails(value) {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const candidate = value;
    if (typeof candidate.getTrackingDetails !== 'function') {
        return undefined;
    }
    return candidate.getTrackingDetails();
}
export function handleError(message, context) {
    let errorContext;
    if (!isErrorContext(context)) {
        errorContext = Object.assign(Object.assign({}, defaultErrorContext), { contexts: { context } });
    }
    else {
        errorContext = Object.assign(Object.assign({}, defaultErrorContext), context);
    }
    if (errorContext.level === 'error') {
        const unwrappedContext = errorContext.contexts ? Object.values(errorContext.contexts) : [];
        // Studio Client global-error-handler
        const tracker = globalThis === null || globalThis === void 0 ? void 0 : globalThis.globalErrorHandler;
        if (errorContext.enabled && tracker) {
            const firstError = unwrappedContext.find(error => error instanceof Error);
            tracker.handleError(firstError !== null && firstError !== void 0 ? firstError : message);
        }
        else {
            console.error(message, ...unwrappedContext);
        }
    }
    else {
        console.warn(message, errorContext.contexts);
    }
}
